import { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { AlertCircle, CheckCircle2, X } from 'lucide-react';
import { useStripeContext } from '@/hooks/useStripeContext';
import {
  type BillingAddress,
  useChangePlan,
  useSubscription,
  useUpgradePlan,
} from '@/hooks/useBilling';
import PaymentMethodForm from './PaymentMethodForm';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

interface Plan {
  id: number;
  name: string;
  slug: string;
  base_price_monthly: string;
  base_price_annual: string;
}

interface UpgradePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: Plan;
}

function formatCurrency(amount: string): string {
  const num = parseFloat(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

export default function UpgradePlanModal({ isOpen, onClose, plan }: UpgradePlanModalProps) {
  const { stripePromise } = useStripeContext();
  const { data: subscription } = useSubscription();
  const upgradeMutation = useUpgradePlan();
  const changePlanMutation = useChangePlan();
  const [billingTerm, setBillingTerm] = useState<'monthly' | 'annual'>('monthly');
  const [step, setStep] = useState<'billing-term' | 'payment'>('billing-term');

  const hasExistingSubscription = !!subscription?.stripe_subscription_id;

  const handlePaymentSuccess = async (
    paymentMethodId: string,
    stripe: import('@stripe/stripe-js').Stripe,
    address?: BillingAddress,
  ) => {
    // Guards the type narrowing rather than the user: PaymentMethodForm is
    // rendered here with collectBillingAddress, and will not call back with an
    // incomplete address.
    if (!address) {
      throw new Error('A billing address is required to start a paid plan.');
    }
    const result = await upgradeMutation.mutateAsync({
      plan_id: plan.id,
      payment_method_id: paymentMethodId,
      billing_term: billingTerm,
      billing_address: address,
    });
    if (result.client_secret) {
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        result.client_secret,
      );
      if (confirmError) {
        throw new Error(confirmError.message ?? 'Payment confirmation failed.');
      }
      if (paymentIntent.status !== 'succeeded') {
        throw new Error(`Payment ${paymentIntent.status}. Please try a different card.`);
      }
    }
    handleClose();
  };

  const handleChangePlan = async () => {
    try {
      await changePlanMutation.mutateAsync({
        new_plan_id: plan.id,
        billing_term: billingTerm,
        prorate: true,
      });
      handleClose();
    } catch (e) {
      console.error('Plan change failed:', e);
    }
  };

  const handleClose = () => {
    setStep('billing-term');
    setBillingTerm('monthly');
    onClose();
  };

  const handleContinue = () => {
    if (hasExistingSubscription) {
      void handleChangePlan();
    } else {
      setStep('payment');
    }
  };

  const selectedPrice =
    billingTerm === 'monthly' ? plan.base_price_monthly : plan.base_price_annual;
  const monthlyRate =
    billingTerm === 'annual'
      ? (parseFloat(plan.base_price_annual) / 12).toFixed(2)
      : parseFloat(plan.base_price_monthly).toFixed(2);

  const isSuccess = upgradeMutation.isSuccess || changePlanMutation.isSuccess;
  const isError = upgradeMutation.isError || changePlanMutation.isError;
  const errorMsg = upgradeMutation.error?.message ?? changePlanMutation.error?.message;

  return (
    <AppSheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={`${hasExistingSubscription ? 'Change plan to' : 'Upgrade to'} ${plan.name}`}
    >
      <AppSheet.Body>
        {isError ? (
          <Alert variant="destructive" className="mb-4 relative">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>{hasExistingSubscription ? 'Plan change' : 'Upgrade'} failed:</strong>{' '}
              {errorMsg ?? 'An unexpected error occurred.'}
            </AlertDescription>
            <button
              type="button"
              onClick={() => {
                upgradeMutation.reset();
                changePlanMutation.reset();
              }}
              className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
              aria-label="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </Alert>
        ) : null}

        {isSuccess ? (
          <Alert className="border-sev-4 bg-sev-4/10">
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              <strong>
                Successfully {hasExistingSubscription ? 'changed to' : 'upgraded to'}{' '}
                {plan.name}.
              </strong>{' '}
              Your subscription{' '}
              {hasExistingSubscription ? 'has been updated' : 'is now active'}.
            </AlertDescription>
          </Alert>
        ) : null}

        {!isSuccess && step === 'billing-term' ? (
          <>
            <h4 className="text-sm font-semibold mb-1">Choose your billing term</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Select how often you'd like to be billed for the {plan.name} plan.
            </p>

            {subscription && hasExistingSubscription ? (
              <Alert className="mb-4 border-sev-5 bg-sev-5-soft">
                <AlertDescription>
                  <strong>Changing from {subscription.plan_name}.</strong> Your existing
                  payment method will be used. You'll be charged a prorated amount for the
                  plan change.
                </AlertDescription>
              </Alert>
            ) : null}

            <RadioGroup
              value={billingTerm}
              onValueChange={(v) => { setBillingTerm(v as 'monthly' | 'annual'); }}
              className="space-y-2 mb-5"
            >
              <label
                className={cn(
                  'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
                  billingTerm === 'monthly'
                    ? 'border-primary bg-brand-soft/40'
                    : 'border-border',
                )}
              >
                <RadioGroupItem value="monthly" className="mt-0.5" />
                <div className="text-sm">
                  <strong>Monthly</strong> — {formatCurrency(plan.base_price_monthly)}/month
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Pay month-to-month with no commitment.
                  </p>
                </div>
              </label>
              <label
                className={cn(
                  'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
                  billingTerm === 'annual'
                    ? 'border-primary bg-brand-soft/40'
                    : 'border-border',
                )}
              >
                <RadioGroupItem value="annual" className="mt-0.5" />
                <div className="text-sm">
                  <strong>Annual</strong> — {formatCurrency(plan.base_price_annual)}/year
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Save{' '}
                    {formatCurrency(
                      String(
                        parseFloat(plan.base_price_monthly) * 12 -
                          parseFloat(plan.base_price_annual),
                      ),
                    )}{' '}
                    per year (${monthlyRate}/month effective rate).
                  </p>
                </div>
              </label>
            </RadioGroup>

            <div className="rounded-md border border-sev-5/30 bg-sev-5/5 p-4">
              <p className="text-sm font-semibold mb-2">Order summary</p>
              <div className="flex justify-between text-sm">
                <span>
                  {plan.name} plan ({billingTerm})
                </span>
                <span className="font-semibold">{formatCurrency(selectedPrice)}</span>
              </div>
              <hr className="my-2 border-border-subtle" />
              <div className="flex justify-between text-sm">
                <span className="font-semibold">Total</span>
                <span className="font-semibold">{formatCurrency(selectedPrice)}</span>
              </div>
            </div>
          </>
        ) : null}

        {!isSuccess && step === 'payment' ? (
          <>
            <h4 className="text-sm font-semibold mb-1">Payment information</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Enter your payment details to complete the upgrade.
            </p>

            <div className="rounded-md border border-border bg-bg-subtle p-3 mb-4">
              <p className="text-sm font-semibold mb-1">Order summary</p>
              <div className="flex justify-between text-sm">
                <span>
                  {plan.name} plan ({billingTerm})
                </span>
                <span className="font-semibold">{formatCurrency(selectedPrice)}</span>
              </div>
            </div>

            <Elements stripe={stripePromise}>
              <PaymentMethodForm
                onSuccess={(paymentMethodId, stripe, address) =>
                  handlePaymentSuccess(paymentMethodId, stripe, address)
                }
                onCancel={() => { setStep('billing-term'); }}
                submitLabel={`Pay ${formatCurrency(selectedPrice)}`}
                isLoading={upgradeMutation.isPending}
                collectBillingAddress
              />
            </Elements>
          </>
        ) : null}
      </AppSheet.Body>
      <AppSheet.Footer>
        {!isSuccess && step === 'billing-term' ? (
          <>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={changePlanMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleContinue}
              disabled={changePlanMutation.isPending}
            >
              {changePlanMutation.isPending
                ? 'Changing…'
                : hasExistingSubscription
                  ? 'Change plan'
                  : 'Continue to payment'}
            </Button>
          </>
        ) : null}
        {isSuccess ? (
          <Button onClick={handleClose}>Done</Button>
        ) : null}
      </AppSheet.Footer>
    </AppSheet>
  );
}
